import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Modal } from "../modal/Modal";
import { Button } from "../input/Button";
import { ReactComponent as EnterIcon } from "../icons/Enter.svg";
import { ReactComponent as VRIcon } from "../icons/VR.svg";
import { ReactComponent as ShowIcon } from "../icons/Show.svg";
import { ReactComponent as SettingsIcon } from "../icons/Settings.svg";
import styles from "./RoomEntryModal.scss";
import styleUtils from "../styles/style-utils.scss";
import { useCssBreakpoints } from "react-use-css-breakpoints";
import { Column } from "../layout/Column";
import { AppLogo } from "../misc/AppLogo";
import { FormattedMessage } from "react-intl";
import { useMobile } from "../layout/hooks";

export function RoomEntryModal({
  className,
  roomName,
  roomDescription,
  showJoinRoom,
  onJoinRoom,
  showEnterOnDevice,
  onEnterOnDevice,
  showSpectate,
  onSpectate,
  showOptions,
  onOptions,
  ...rest
}) {
  const mobile = useMobile();

  if (!mobile) {
    return (
      <div
        className={classNames(
          "tw-fixed",
          "tw-z-10",
          "tw-top-0",
          "tw-right-1/2",
          "tw-bottom-0",
          "tw-left-0",
          "tw-grid",
          "tw-items-center",
          "tw-bg-[#ffffff]/80",
          "tw-backdrop-blur-[5px]"
        )}
        style={{
          background: "rgba"
        }}
      >
        <div className={classNames("tw-max-w-2xl", "tw-px-20", "tw-mx-auto", "tw-gap-4")}>
          <div className={classNames("tw-grid", "tw-mb-10")}>
            <div className={classNames("tw-text-6xl", "tw-font-black", "tw-break-all")}>{roomName}</div>
            {roomDescription && (
              <div className={classNames("tw-text-lg", "tw-mt-2", "tw-font-medium")}>{roomDescription}</div>
            )}
            <br></br>
            <div target="_blank" href="https://lp.meta-tabi.jp/fukushima">
              ※注意事項
              メタバース内の画像はクリック・タップが出来るようになっていますが、ポップアップブロックが設定されていると正常に動作しない場合がございます。解除手順は次のリンクからご確認ください。
              プレオープン中は各リンクが正常に動作しない、またはリンク先が異なる場合もございますので予めご了承ください。
            </div>
            <br></br>
            <div
              target="_blank"
              href="https://support.apple.com/ja-jp/HT203987#:~:text=iPhone%20%E3%81%BE%E3%81%9F%E3%81%AF%20iPad%20%E3%81%A7%E3%81%AF%E3%80%81%E3%80%8C%E8%A8%AD%E5%AE%9A,%E3%83%96%E3%83%AD%E3%83%83%E3%82%AF%E3%80%8D%E3%82%92%E3%82%AA%E3%83%B3%E3%81%AB%E3%81%97%E3%81%BE%E3%81%99%E3%80%82"
            >
              Safariをご利用の場合は
              <a
                target="_blank"
                href="https://support.apple.com/ja-jp/HT203987#:~:text=iPhone%20%E3%81%BE%E3%81%9F%E3%81%AF%20iPad%20%E3%81%A7%E3%81%AF%E3%80%81%E3%80%8C%E8%A8%AD%E5%AE%9A,%E3%83%96%E3%83%AD%E3%83%83%E3%82%AF%E3%80%8D%E3%82%92%E3%82%AA%E3%83%B3%E3%81%AB%E3%81%97%E3%81%BE%E3%81%99%E3%80%82"
                rel="noreferrer"
              >
                こちら
              </a>
              <br></br>
              Google Chromeをご利用の場合は
              <a
                target="_blank"
                href="https://support.google.com/chrome/answer/95472?hl=ja&co=GENIE.Platform%3DDesktop"
                rel="noreferrer"
              >
                こちら
              </a>
            </div>

            <br></br>
            <a target="_blank" href="https://lp.meta-tabi.jp/terms" rel="noreferrer">
              利用規約はこちら
            </a>
          </div>

          <div
            className={classNames(
              "tw-grid",
              "tw-gap-5",
              "tw-grid-cols-[minmax(auto,280px)_minmax(auto,280px)]",
              "tw-mx-auto"
            )}
          >
            {showJoinRoom && (
              <Button block preset="accent4" onClick={onJoinRoom}>
                <FormattedMessage id="room-entry-modal.join-room-button" defaultMessage="Join Room" />
              </Button>
            )}
            {showEnterOnDevice && (
              <Button block preset="accent5" onClick={onEnterOnDevice}>
                <FormattedMessage id="room-entry-modal.enter-on-device-button" defaultMessage="Enter On Device" />
              </Button>
            )}
          </div>
          <div
            className={classNames(
              "tw-absolute",
              "tw-bottom-2",
              "tw-left-0",
              "tw-right-0",
              "tw-flex",
              "tw-justify-center",
              "tw-text-sm"
            )}
          >
            Presented by KAGEYAMA CORPORATION. Powered by Suishow, Inc.
          </div>
        </div>
      </div>
    );
  }

  return (
    <Modal className={classNames(styles.roomEntryModal, className)} disableFullscreen {...rest}>
      <Column className={classNames(styles.content, "tw-p-6")}>
        <div>
          <div>
            <div className={classNames("tw-text-2xl", "tw-font-[#000000]", "tw-font-black")}>{roomName}</div>
            {roomDescription ? (
              <>
                <div className={classNames("tw-mt-2", "tw-text-lg", "tw-font-medium")}>{roomDescription} </div>
                <div className={classNames("tw-h-8")} />
              </>
            ) : (
              <div className={classNames("tw-h-16")} />
            )}
            <a target="_blank" href="https://lp.meta-tabi.jp/fukushima" rel="noreferrer">
              利用規約はこちら
            </a>
            <br></br>
          </div>

          <div target="_blank" href="https://lp.meta-tabi.jp/fukushima">
            <br></br>
            ※注意事項
            メタバース内の画像はクリック・タップが出来るようになっていますが、ポップアップブロックが設定されていると正常に動作しない場合がございます。解除手順は次のリンクからご確認ください。
            プレオープン中は各リンクが正常に動作しない、またはリンク先が異なる場合もございますので予めご了承ください。
          </div>
          <br></br>
          <div
            target="_blank"
            href="https://support.apple.com/ja-jp/HT203987#:~:text=iPhone%20%E3%81%BE%E3%81%9F%E3%81%AF%20iPad%20%E3%81%A7%E3%81%AF%E3%80%81%E3%80%8C%E8%A8%AD%E5%AE%9A,%E3%83%96%E3%83%AD%E3%83%83%E3%82%AF%E3%80%8D%E3%82%92%E3%82%AA%E3%83%B3%E3%81%AB%E3%81%97%E3%81%BE%E3%81%99%E3%80%82"
          >
            Safariをご利用の場合は
            <a
              target="_blank"
              href="https://support.apple.com/ja-jp/HT203987#:~:text=iPhone%20%E3%81%BE%E3%81%9F%E3%81%AF%20iPad%20%E3%81%A7%E3%81%AF%E3%80%81%E3%80%8C%E8%A8%AD%E5%AE%9A,%E3%83%96%E3%83%AD%E3%83%83%E3%82%AF%E3%80%8D%E3%82%92%E3%82%AA%E3%83%B3%E3%81%AB%E3%81%97%E3%81%BE%E3%81%99%E3%80%82"
              rel="noreferrer"
            >
              こちら
            </a>
            <br></br>
            Google Chromeをご利用の場合は
            <a
              target="_blank"
              href="https://support.google.com/chrome/answer/95472?hl=ja&co=GENIE.Platform%3DDesktop"
              rel="noreferrer"
            >
              こちら
            </a>
          </div>

          <br></br>
          <div className={classNames("tw-flex", "portrait:tw-flex-col", "tw-gap-4")}>
            {showJoinRoom && (
              <div className={classNames("tw-flex-1", "tw-flex", "tw-flex-col")}>
                <Button className={classNames("tw-min-w-[172px]")} preset="accent4" onClick={onJoinRoom}>
                  <FormattedMessage id="room-entry-modal.join-room-button" defaultMessage="Join Room" />
                </Button>
              </div>
            )}
            {showEnterOnDevice && (
              <div className={classNames("tw-flex-1", "tw-flex", "tw-flex-col")}>
                <Button className={classNames("tw-min-w-[172px]")} preset="accent5" onClick={onEnterOnDevice}>
                  <FormattedMessage id="room-entry-modal.enter-on-device-button" defaultMessage="Enter On Device" />
                </Button>
              </div>
            )}
          </div>
          <br></br>
        </div>
      </Column>
      <div className={classNames("tw-absolute", "tw-top-full", "tw-left-1/2", "-tw-translate-x-1/2")}>
        <div className={classNames("tw-pt-3", "tw-text-xs", "tw-text-center", "tw-whitespace-nowrap")}>
          Presented by KAGEYAMA CORPORATION. Powered by Suishow, Inc.
        </div>
      </div>
    </Modal>
  );
}

RoomEntryModal.propTypes = {
  className: PropTypes.string,
  roomName: PropTypes.string.isRequired,
  roomDescription: PropTypes.string,
  showJoinRoom: PropTypes.bool,
  onJoinRoom: PropTypes.func,
  showEnterOnDevice: PropTypes.bool,
  onEnterOnDevice: PropTypes.func,
  showSpectate: PropTypes.bool,
  onSpectate: PropTypes.func,
  showOptions: PropTypes.bool,
  onOptions: PropTypes.func
};

RoomEntryModal.defaultProps = {
  showJoinRoom: true,
  showEnterOnDevice: true,
  showSpectate: true,
  showOptions: true
};
